import { useCallback, useState } from 'react';
import { t, Trans } from '@lingui/macro';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import { Column } from '@decisiv/ui-components/lib/components/Table/types';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import Loading from 'components/Loading';
import useSort from 'utils/useSort';

import { useCurrentUserContext } from 'utils/User';
import PaginationFooter from 'components/PaginationFooter';
import SearchableFilter from 'components/SearchableFilter';
import { parseDate } from 'utils/dates';
import ActionsCell from 'pages/Teams/Cells/ActionsCell';
import NameCell from 'pages/Teams/Cells/NameCell';
import PlatformAccountsCell from 'pages/Teams/Cells/PlatformAccountsCell';
import TeamModalProvider from 'pages/Teams/CreateTeamModal/contexts/TeamModalContext';
import CreateTeamModal from 'pages/Teams/CreateTeamModal';
import TeamTypeCell from 'pages/Teams/Cells/TeamTypeCell';
import useFooter from 'utils/useFooter';
import {
  MinWidthSpan,
  NoWrapHeaderTable,
  RightAlignedFlex,
} from '../components/styles';
import { useManagedSentinelTeamsQuery } from './hooks/useManagedSentinelTeamsQuery';

import { FiltersContainer } from './styles';
import { useManagementPageContext } from '../contexts/ManagementPageContext';
import useOemSegmentsFilters from '../OemSegments/hooks/useOemSegmentsFilters';
import useTeamsFilters from './hooks/useTeamsFilters';

type ManagedTeamsType =
  ManagedSentinelTeamsQuery['managedSentinelTeams']['collection'][0];
type ColumnProperties = Pick<
  ManagedTeamsType,
  | 'id'
  | 'name'
  | 'totals'
  | 'enabled'
  | 'createdAt'
  | 'createdBy'
  | 'teamKey'
  | 'teamType'
  | 'owner'
  | 'apiUsername'
  | 'platformAccounts'
  | 'scheduledForDeletionAt'
  | 'oemSegment'
>;

const Teams = () => {
  const { footerRef } = useFooter();
  const { isSuperUser } = useCurrentUserContext();

  const { pageParams, setPageParams } = useManagementPageContext();

  const [page, setPage] = useState(1);

  const [{ sort }, { getSortProps }] = useSort();
  const { defaultSort, onSort } = getSortProps();

  const { data, loading } = useManagedSentinelTeamsQuery({
    variables: {
      sort,
      page,
      ids: pageParams.teamIds,
      oemSegmentIds: pageParams.oemSegmentIds,
    },
  });

  const handleOnSubmitOemSegments = useCallback(
    ({ value }) =>
      setPageParams((oldState) => ({
        ...oldState,
        oemSegmentIds: value as string[],
      })),
    [setPageParams],
  );

  const handleOnSubmitTeams = useCallback(
    ({ value }) =>
      setPageParams((oldState) => ({
        ...oldState,
        teamIds: value as string[],
      })),
    [setPageParams],
  );

  const oemSegmentsFilterProps = useOemSegmentsFilters({
    value: pageParams.oemSegmentIds,
    onSubmit: handleOnSubmitOemSegments,
  });

  const teamFilterProps = useTeamsFilters({
    value: pageParams.teamIds,
    onSubmit: handleOnSubmitTeams,
  });

  const columns: Column<ColumnProperties>[] = [
    {
      name: 'oemSegmentName',
      title: t`OEM Segment Name`,
      DataCell: ({ rowData: { oemSegment } }) => (
        <MinWidthSpan $minWidth={160}>{oemSegment?.name ?? '-'}</MinWidthSpan>
      ),
      sortBy: (direction) => onSort('OEM_SEGMENT_NAME', direction),
    },
    {
      name: 'name',
      title: t`Team Name`,
      DataCell: ({ rowData }) => <NameCell hideLogo rowData={rowData} />,
      sortBy: (direction) => onSort('NAME', direction),
    },
    {
      name: 'id',
      title: t`Team ID`,
      DataCell: ({ rowData: { id } }) => (
        <MinWidthSpan $minWidth={80}>{id}</MinWidthSpan>
      ),
      sortBy: (direction) => onSort('ID', direction),
    },
    {
      name: 'teamKey',
      title: t`Team Key`,
      DataCell: ({ rowData: { teamKey } }) => (
        <MinWidthSpan $minWidth={90}>{teamKey}</MinWidthSpan>
      ),
      sortBy: (direction) => onSort('TEAM_KEY', direction),
    },
    {
      name: 'teamType',
      title: t`Team Type`,
      DataCell: ({ rowData }) => (
        <MinWidthSpan $minWidth={100}>
          <TeamTypeCell rowData={rowData} />
        </MinWidthSpan>
      ),
      sortBy: (direction) => onSort('TEAM_TYPE', direction),
    },
    {
      name: 'createdBy',
      title: t`Created By`,
      DataCell: ({ rowData: { createdBy } }) => (
        <MinWidthSpan $minWidth={100}>{createdBy?.name ?? '-'}</MinWidthSpan>
      ),
      sortBy: (direction) => onSort('CREATED_BY_NAME', direction),
    },
    {
      name: 'createdAt',
      title: t`Created On`,
      DataCell: ({ rowData: { createdAt } }) => (
        <MinWidthSpan $minWidth={110}>{parseDate(createdAt)}</MinWidthSpan>
      ),
      sortBy: (direction) => onSort('CREATED_AT', direction),
    },
    {
      name: 'platformAccounts',
      title: t`Accounts`,
      DataCell: ({ rowData: { totals } }) => (
        <MinWidthSpan $minWidth={100}>{totals?.platformAccounts}</MinWidthSpan>
      ),
      sortBy: (direction) => onSort('PLATFORM_ACCOUNTS_COUNT', direction),
    },
    {
      name: 'portalId',
      title: t`Account ID`,
      DataCell: PlatformAccountsCell,
      sortBy: (direction) => onSort('DEALER_GROUP_ID', direction),
    },
    {
      name: 'assets',
      title: t`Assets`,
      DataCell: ({ rowData: { totals } }) => (
        <MinWidthSpan $minWidth={80}>
          {totals?.assetGroupings?.enabledAssets}
        </MinWidthSpan>
      ),
      sortBy: (direction) => onSort('ENABLED_ASSET_COUNT', direction),
    },
    {
      name: 'members',
      title: t`Team Members`,
      DataCell: ({ rowData: { totals } }) => (
        <MinWidthSpan $minWidth={110}>{totals?.members?.total}</MinWidthSpan>
      ),
      sortBy: (direction) => onSort('MEMBERS_COUNT', direction),
    },
    {
      name: 'owner',
      title: t`Owner`,
      DataCell: ({ rowData: { owner } }) => (
        <MinWidthSpan $minWidth={80}>
          {owner?.username ?? owner.email}
        </MinWidthSpan>
      ),
      sortBy: (direction) => onSort('OWNER_USERNAME', direction),
    },
    {
      name: 'apiUsername',
      title: t`API User`,
      DataCell: ({ rowData: { apiUsername } }) => (
        <span>{apiUsername ?? '-'}</span>
      ),
      sortBy: (direction) => onSort('API_USER', direction),
    },
    {
      fixed: true,
      name: 'enabled',
      title: t`Status`,
      DataCell: ActionsCell,
    },
  ];

  const teams = data?.managedSentinelTeams?.collection ?? [];
  const pagination = data?.managedSentinelTeams?.pagination;

  return (
    <Flex
      flex={1}
      flexDirection="column"
      style={{ position: 'relative', height: '100%' }}
    >
      <Flex marginX={2} marginY={1} justifyContent="space-between">
        {isSuperUser && (
          <TeamModalProvider>
            <CreateTeamModal />
          </TeamModalProvider>
        )}
        <RightAlignedFlex alignItems="center">
          <P size="small" as="span" shade={1}>
            <Trans>Search Teams by Name</Trans>
          </P>
          <SearchInput name="searchByTeam" size="small" value="" />
        </RightAlignedFlex>
      </Flex>
      <FiltersContainer paddingX={2} paddingY={1}>
        <SearchableFilter
          popoverLabel={t`OEM Segments`}
          placement="bottom-start"
          {...oemSegmentsFilterProps}
        />
        <SearchableFilter popoverLabel={t`Teams`} {...teamFilterProps} />
      </FiltersContainer>
      {loading && <Loading />}
      {!loading && (
        <NoWrapHeaderTable
          height="100%"
          data={teams}
          scroll={['x', 'y']}
          columns={columns}
          onSort={onSort}
          defaultSort={defaultSort}
          footer={() => (
            <PaginationFooter
              ref={footerRef}
              title={t`Teams`}
              setCurrentPage={setPage}
              totalOnPage={teams?.length}
              page={pagination?.page}
              perPage={pagination?.perPage}
              totalEntries={pagination?.totalEntries}
              totalPages={pagination?.totalPages}
            />
          )}
        />
      )}
    </Flex>
  );
};

export default Teams;
